/**
 * AB#403 — Claire's Quick-Add state machine.
 *
 * Custom element <claires-quick-add> wrapping an "Add to Bag" button.
 * Handles:
 *   - POST /cart/add.js on click
 *   - State transitions: idle -> loading -> added
 *   - Restoring "added" state on page load if the variant is already in cart
 *   - Broadcasting claires:cart-updated event so other instances + the header
 *     cart count stay in sync across the page
 *   - Auto-opening Dawn's <cart-drawer> on successful add
 *
 * Used by:
 *   - PLP product cards (AB#400)
 *   - PDP main product section (AB#401)
 *
 * All customer-facing labels are passed in via data-* attributes from the
 * Liquid snippet (snippets/claires-quick-add-button.liquid), which reads
 * them from theme settings. NO hardcoded customer-facing strings in this
 * file. Aligns with `feedback_admin_editable_default`.
 */
(function () {
  "use strict";

  if (customElements.get("claires-quick-add")) return;

  // Page-shared cart cache: one /cart.js fetch per page load instead of
  // refetching per element. Invalidated whenever any instance successfully
  // adds an item.
  var cartCache = null;
  var cartCacheLoading = null;

  function getCart() {
    if (cartCache) return Promise.resolve(cartCache);
    if (cartCacheLoading) return cartCacheLoading;
    cartCacheLoading = fetch("/cart.js", {
      headers: { Accept: "application/json" },
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        cartCache = data;
        cartCacheLoading = null;
        return data;
      })
      .catch(function () {
        cartCacheLoading = null;
        return { items: [] };
      });
    return cartCacheLoading;
  }

  function invalidateCart() {
    cartCache = null;
  }

  class ClairesQuickAdd extends HTMLElement {
    constructor() {
      super();
      this.state = "idle";
    }

    connectedCallback() {
      this.button = this.querySelector("button.claires-quick-add__button");
      if (!this.button) return;
      this.labelEl = this.button.querySelector(".claires-quick-add__label");
      this.onClickBound = this.onClick.bind(this);
      this.onCartUpdateBound = this.onCartUpdate.bind(this);
      this.button.addEventListener("click", this.onClickBound);
      document.addEventListener(
        "claires:cart-updated",
        this.onCartUpdateBound
      );
      this.hydrateInitialState();
    }

    disconnectedCallback() {
      if (this.button) {
        this.button.removeEventListener("click", this.onClickBound);
      }
      document.removeEventListener(
        "claires:cart-updated",
        this.onCartUpdateBound
      );
    }

    get variantId() {
      return this.getAttribute("data-variant-id");
    }

    get buttonStyle() {
      return this.getAttribute("data-button-style") || "card";
    }

    hydrateInitialState() {
      var variantId = this.variantId;
      if (!variantId) return;
      // AB#869: respect the server-rendered Sold Out state — don't override it from the cart.
      if (this.button && this.button.dataset.state === "sold-out") {
        this.state = "sold-out";
        return;
      }
      var self = this;
      getCart().then(function (cart) {
        if (self.state === "loading") return;
        var inCart =
          cart.items &&
          cart.items.some(function (item) {
            return String(item.variant_id) === String(variantId);
          });
        // AB#866: revert to idle ("Add to Bag") when the item is no longer in the cart
        // (e.g. removed from the drawer) — not just set "added" when present.
        self.setState(inCart ? "added" : "idle");
      });
    }

    onCartUpdate(event) {
      // Sync state across sibling instances when one of them updates the
      // cart. Skip the event the current instance just dispatched.
      if (event && event.detail && event.detail.source === this) return;
      this.hydrateInitialState();
    }

    setState(next) {
      this.state = next;
      this.button.dataset.state = next;
      this.button.disabled = next === "loading";
      this.updateLabel();
    }

    updateLabel() {
      if (!this.labelEl) return;
      var label = "";
      if (this.state === "idle") {
        label = this.button.dataset.labelIdle || "";
      } else if (this.state === "loading") {
        label = this.button.dataset.labelLoading || "";
      } else if (this.state === "added") {
        label =
          this.buttonStyle === "pdp"
            ? this.button.dataset.labelAddedPdp || ""
            : this.button.dataset.labelAddedCard || "";
      }
      this.labelEl.textContent = label;
    }

    onClick(event) {
      event.preventDefault();
      event.stopPropagation();
      if (this.state === "loading" || !this.variantId) return;

      this.setState("loading");
      var self = this;
      var variantId = this.variantId;

      // AB#899: read the PDP quantity stepper so multi-qty adds work (cards have no stepper -> 1)
      var quantity = 1;
      if (self.buttonStyle === "pdp") {
        var qtyRow = self.button.closest(".claires-pdp__purchase-row");
        var qtyInput = qtyRow && qtyRow.querySelector(".claires-pdp__qty-input");
        if (qtyInput) { quantity = Math.max(1, parseInt(qtyInput.value, 10) || 1); }
      }
      fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        // AB#859: request the cart-drawer + cart-icon-bubble sections so the add
        // response carries fresh HTML to repaint the drawer + header count.
        body: JSON.stringify({
          id: variantId,
          quantity: quantity,
          sections: ["cart-drawer", "cart-icon-bubble"],
          sections_url: window.location.pathname,
        }),
      })
        .then(function (response) {
          if (!response.ok) {
            throw new Error("/cart/add.js " + response.status);
          }
          return response.json();
        })
        .then(function (addResponse) {
          invalidateCart();
          self.setState("added");
          document.dispatchEvent(
            new CustomEvent("claires:cart-updated", {
              bubbles: true,
              detail: { source: self, variantId: variantId },
            })
          );
          self.openCartDrawer(addResponse);
        })
        .catch(function (err) {
          console.error("[claires-quick-add] add failed:", err);
          self.setState("idle");
        });
    }

    openCartDrawer(addResponse) {
      var drawer = document.querySelector("cart-drawer");
      if (!drawer) return; // cart_type 'page'/'notification' — count still syncs via event
      // AB#859: the <cart-drawer> element keeps the `is-empty` class it was rendered
      // with at page load, and that class CSS-hides the line items. Clear it on the
      // element (renderContents only clears it from .drawer__inner) — mirrors Dawn
      // product-form.js. Without this the drawer opens with a total but no products.
      drawer.classList.remove("is-empty");
      if (
        typeof drawer.renderContents === "function" &&
        addResponse &&
        addResponse.sections
      ) {
        // Repaint drawer + header count from the add.js sections payload, then open.
        drawer.renderContents(addResponse);
      } else if (typeof drawer.open === "function") {
        drawer.open();
      }
    }
  }

  customElements.define("claires-quick-add", ClairesQuickAdd);

  // AB#866: when the cart changes elsewhere (e.g. an item removed in the cart drawer),
  // Dawn publishes cartUpdate. Refresh the cached cart and re-broadcast claires:cart-updated
  // so every card's button reverts from "Add Another" back to "Add to Bag" when its item is gone.
  if (
    typeof subscribe === "function" &&
    typeof PUB_SUB_EVENTS !== "undefined" &&
    PUB_SUB_EVENTS.cartUpdate
  ) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      invalidateCart();
      document.dispatchEvent(
        new CustomEvent("claires:cart-updated", { bubbles: true, detail: {} })
      );
    });
  }
})();
